/// <amd-module name="Feed/Legacy/Extensions/Package.Hub" />
import * as React from "react";
import { connect } from "react-redux";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { HistoryServiceEventNames, INavigationStateChangedEvent, IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { isPageInteractive, TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";

import { CiConstants, DataProviderConstants, ErrorCi, FeedServiceInstanceId } from "Feed/Common/Constants/Constants";
import { WebPageData } from "Feed/Common/Contracts/WebPage";
import { FeedDialog } from "Feed/Core/Components/FeedDialog";
import { FeedMessageBar } from "Feed/Core/Components/FeedMessageBar";
import { IFeedHubViewAwareState } from "Feed/Core/Redux/FeedHub";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { getHubState, NavigationActions } from "Feed/Core/Redux/Navigation";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { IFeedProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";
import { FeedStore } from "Feed/Legacy/Stores/FeedStore";
import { GlobalState, GlobalStore } from "Feed/Legacy/Stores/GlobalStore";
import { PackageStore } from "Feed/Legacy/Stores/PackageStore";
import { IHubState } from "Feed/Legacy/Types/IHubState";
import { toHubState } from "Feed/Legacy/Utils/Hub";

import { ConnectedFeedContentComponent } from "../Components/FeedContentComponent";
import { WrappedGlobalSettingsPanel } from "../Components/WrappedGlobalSettingsPanel";

interface IPackageHubState {
    isJsonIslandDataAvailable: boolean;
}

interface IPackageHubProps {
    // #1593396 these are only used for legacy compat
    isPageInteractive: boolean;
    hubState: IHubState;
}

const Actions = {
    addSplitTiming: TTIMonitorActions.addSplit,
    markTTI: TTIMonitorActions.markTTI,
    navigateToHub: NavigationActions.navigateToHub,
    setErrorMessage: FeedHubActions.setErrorMessage
};

export class PackageHub extends VssComponent<IPackageHubProps & typeof Actions, IPackageHubState> {
    public constructor(
        props: IVssComponentProperties & IPackageHubProps & typeof Actions,
        context?: IVssComponentContext
    ) {
        super(props, context);

        this.props.addSplitTiming("ms-feed.mainView.render.start");

        let isJsonIslandDataAvailable = false;
        if (this.isWebPageDataAvailable()) {
            this.ProtocolProvider.initializeProtocols();
            this.initializeStores(this.props.hubState);
            isJsonIslandDataAvailable = true;
        } else {
            this.getJsonIslandDataAsync().then(() => {
                this.ProtocolProvider.initializeProtocols();
                this.initializeStores(this.props.hubState);
                this.setState({ isJsonIslandDataAvailable: true });
            });
        }

        this.state = {
            isJsonIslandDataAvailable
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        // if data is available when page loaded, publish TTI now
        if (this.state.isJsonIslandDataAvailable) {
            this.postComponentRender();
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this.globalStore.removeChangedListener(this.storeEmitChanged);

        this.globalStore.dispose();
        this.feedStore.dispose();
        this.packageStore.dispose();

        this.HistoryService.unsubscribe(this.navigationHandler, HistoryServiceEventNames.statePopped);
    }

    public componentDidUpdate(): void {
        // if data wasn't available on page loaded and retrieved async from client, publish TTI now
        if (this.state.isJsonIslandDataAvailable && this.props.isPageInteractive === false) {
            this.postComponentRender();
        }
    }

    public render(): JSX.Element {
        // add an error component if necessary
        return this.state.isJsonIslandDataAvailable ? (
            <div className="packages-hub flex flex-column flex-grow">
                <FeedMessageBar />
                <FeedDialog />
                <ConnectedFeedContentComponent feedStore={this.feedStore} packageStore={this.packageStore} />
                <WrappedGlobalSettingsPanel />
            </div>
        ) : (
                <LoadingContainer isLoading={true} />
            );
    }

    private postComponentRender() {
        this.attachNavigationListeners();

        this.globalStore.addChangedListener(this.storeEmitChanged);

        this.props.addSplitTiming("ms-feed.mainView.render.end");
        this.props.markTTI(CiConstants.Area, "mainView", {
            action: this.props.hubState.action,
            serviceInstanceType: FeedServiceInstanceId
        });
    }

    private attachNavigationListeners(): void {
        this.navigationHandler = (value: INavigationStateChangedEvent): void => {
            let newHubState = value.newState!.data as IHubState;
            if (newHubState.action == undefined) {
                newHubState = toHubState(value.newState!.state);
            }
            if (newHubState.action != undefined) {
                // some navigation e.g. pivots is handled elsewhere
                this.processNavigation(newHubState);
            }
        };
        this.HistoryService.subscribe(this.navigationHandler, HistoryServiceEventNames.statePopped);
    }

    /**
     * Data provider doesn't come down in json island all the time, if TFS's get dataprovider request to Feed service
     * takes more than 3 seconds, request will be cancelled. In this case, getData will return undefined.
     * Dataprovider call will take longer time,
     *  1. when feed service was dormant
     *  2. takes longer time to fetch data from SQL
     *  3. we are doing too much work in data provider
     */
    private async getJsonIslandDataAsync(): Promise<void> {
        CustomerIntelligenceHelper.publishEvent(this.context.pageContext, ErrorCi.PackageHubDataProviderIsEmpty);
        const contributionService = this.context.pageContext.getService<IVssContributionService>(
            "IVssContributionService"
        );
        await contributionService.getDataAsync<WebPageData>(
            DataProviderConstants.PackageHubDataProvider,
            null /* properties */,
            true /* ignoreCache */,
            FeedServiceInstanceId
        );
    }

    private isWebPageDataAvailable() {
        const webPageDataService = this.context.pageContext.getService<HubWebPageDataService>(
            "IFeedWebPageDataService"
        );
        return webPageDataService.isWebPageDataAvailable();
    }

    private initializeStores(state: IHubState): void {
        this.globalStore = new GlobalStore(this.context.pageContext);
        this.feedStore = new FeedStore(this.context.pageContext, state);
        this.packageStore = new PackageStore(
            this.context.pageContext,
            state,
            this.feedStore.getCurrentFeed.bind(this.feedStore),
            this.feedStore.getCurrentFeedViews.bind(this.feedStore)
        );
    }

    private async processNavigation(hubState: IHubState): Promise<void> {
        try {
            await this.feedStore.setHubState(hubState.action, hubState);
            this.packageStore.setHubState(hubState.action, hubState);
        } catch (error) {
            this.props.setErrorMessage(error);
        } finally {
            this.props.navigateToHub(hubState.action);
        }
    }

    private get GlobalState(): GlobalState {
        return this.globalStore.getGlobalState();
    }

    private storeEmitChanged = () => {
        // trigger render
        this.setState({});
    };

    private get ProtocolProvider(): IFeedProtocolProvider {
        return this.context.pageContext.getService<IFeedProtocolProvider>("IFeedProtocolProvider");
    }

    // tslint:disable-next-line:adjacent-overload-signatures
    private get HistoryService(): IVssHistoryService {
        return this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    private globalStore: GlobalStore;
    private feedStore: FeedStore;
    private packageStore: PackageStore;
    private navigationHandler: (value: INavigationStateChangedEvent, action?: string | undefined) => void;
}

function mapStateToProps(state: IFeedHubViewAwareState): IPackageHubProps {
    return {
        isPageInteractive: isPageInteractive(state),
        hubState: getHubState(state) as IHubState
    };
}

export const ConnectedPackageHub = connect(mapStateToProps, Actions)(PackageHub);
